import { db } from '@/plugins/firebase';
import * as DateTime from '@/utils/date';

const reserve_collectionName = "reserve";

const POINT_PER_MINUTES = 50;

export const STATUS = {
  "OPEN": "OPEN",
  "RESERVED": "RESERVED",
  "NOW_ON_AIR": "NOW_ON_AIR",
  "TERMINATE": "TERMINATE"
};

export async function createReserveDoc(liveId, startAt, endAt) {
  let list = {};
  let trimmedStartAt = startAt;
  let trimmedEndAt = endAt;
  trimmedStartAt.setMilliseconds(0);
  trimmedEndAt.setMilliseconds(0);

  let itemStart = new Date(trimmedStartAt.valueOf());

  while (itemStart < trimmedEndAt) {
      let itemEnd = new Date(itemStart.valueOf());
      itemEnd.setMinutes(itemEnd.getMinutes() + 10)

      let doc = {
        reserver: null,
        point: 0,
        status: STATUS.OPEN,
        startAt: itemStart,
        endAt: itemEnd < trimmedEndAt ? itemEnd : trimmedEndAt,
        realStartAt: null,
        realEndAt: null,
      }

      doc.startAt.setMilliseconds(0);
      doc.endAt.setMilliseconds(0);

      list[DateTime.format("yyyyMMddHHmm", DateTime.fromDate(itemStart))] = doc;
      itemStart = itemEnd;
  }

  return updateReserveDoc(liveId, list);
}

export async function hasReserve(liveId) {
  let doc = await getReserveDoc(liveId);
  return doc && doc.list && Object.keys(doc.list).length > 0;
}

export async function getReserve(liveId) {
  let doc = await getReserveDoc(liveId);
  let result = []
  if (!doc || !doc.list) {
    return result;
  }

  for (let key in doc.list) {
    result.push(convertToPayload(key, doc.list[key], liveId));
  }

  result.sort((item1, item2) => { return item1.startAt - item2.startAt; } );
  return result;
}

export async function getMargedReserve(liveId) {
  let list = await getReserve(liveId);
  if (!list || !list.length > 0) {
    return [];
  }

  let margedList = [];
  let item = null;
  for (let doc of list) {
    if (!item || item.reserver != doc.reserver) {
      if (item) {
        item.docList.sort((item1, item2) => { return item1.startAt - item2.startAt; } );
        margedList.push(item);
      }
      item = {
        reserveId: doc.id,
        reserver: doc.reserver,
        startAt: doc.startAt,
        endAt: doc.endAt,
        status: doc.status,
        docList: [doc]
      }
    } else {
      item.endAt = doc.endAt;
      item.docList.push(doc);
    }
  }
  if (item) margedList.push(item);
  margedList.sort((item1, item2) => { return item1.startAt - item2.startAt; } );

  return margedList;
}

export async function getReserveById(liveId, id) {
  let doc = await getReserveDoc(liveId);

  for (let key in doc.list) {
    if (key == id) return convertToPayload(key, doc.list[key], liveId); 
  }

  return null;
}

export async function getReserveByUserId(userId) {
  let lives = [];

  const reserveQuerySnapshot = await db
    .collection(reserve_collectionName)
    .where('isEnded', '==', false)
    .get();

  if (reserveQuerySnapshot.empty) {
    return lives;
  } else {
    let criterionDate = (await DateTime.serverDate()).toJSDate();

    reserveQuerySnapshot.forEach(doc => {
      const data = doc.data();

      for (let key in data.list) {
        const item = data.list[key];
        if (item.reserver == userId) {
          if (item.endAt.toDate() >= criterionDate) {
            lives.push(convertToPayload(key, item, data.liveId));          
          }
        }
      }
    });
  }
  return lives;
}

export async function getReservePoint(userId) {
  let count = 0;

  let reserveList = await getReserveByUserId(userId);
  for (let reserve of reserveList) {
    if (reserve.status == STATUS.RESERVED || reserve.status == STATUS.NOW_ON_AIR) {
      count = count + reserve.reservePoint;
    }
  }

  return count;
}

export async function reserve(userId, liveId, idList) {
  let result = true;
  let criterionDate = (await DateTime.serverDate()).toJSDate();
  criterionDate.setMinutes(criterionDate.getMinutes() + 5);

  for (let id of idList) {
    await execReserve(userId, criterionDate, {
      liveId: liveId,
      id: id
    }).catch(() => result = false);
  }

  return result;
}

async function execReserve(userId, criterionDate, payload) {
  return await updateReserve(payload.liveId, payload.id, (doc) => {
    if (doc.status !== STATUS.OPEN || criterionDate >= doc.startAt.toDate()) {
      throw "failed";
    }
    let newDoc = doc;
    newDoc.reserver = userId;
    newDoc.status = STATUS.RESERVED;
    return doc;
  });
}

export async function cancel(payload) {
  if (payload.status !== STATUS.RESERVED) {
    return;
  }

  return await updateReserve(payload.liveId, payload.id, (doc) => {
    let newDoc = doc;
    newDoc.reserver = null;
    newDoc.status = STATUS.OPEN;
    return newDoc;
  });
}

export async function onAir(payload) {
  if (payload.status !== STATUS.RESERVED) {
    return;
  }

  return await updateReserve(payload.liveId, payload.id, async (doc) => {
    let newDoc = doc;
    newDoc.status = STATUS.NOW_ON_AIR;
    newDoc["realStartAt"] = (await DateTime.serverDate()).toJSDate()
    return newDoc;
  });
}

export async function terminate(payload) {
  if (payload.status !== STATUS.NOW_ON_AIR) {
    return;
  }

  return await updateReserve(payload.liveId, payload.id, async (doc) => {
    let newDoc = doc;
    newDoc.status = STATUS.TERMINATE;
    newDoc["realEndAt"] = (await DateTime.serverDate()).toJSDate()
    return newDoc;
  });
}

export async function countUp(payload) {
  if (payload.status !== STATUS.NOW_ON_AIR && payload.reservePoint < payload.point + POINT_PER_MINUTES) {
    return;
  }

  return await updateReserve(payload.liveId, payload.id, (doc) => {
    let newDoc = doc;
    let reservePoint = payload.reservePoint;
    newDoc.point += POINT_PER_MINUTES;
    if (newDoc.point > reservePoint) newDoc.point = reservePoint;
    return newDoc;
  });
}

export async function deleteReserve(liveId) {
  let success = true;

  await db.collection(reserve_collectionName).doc(liveId).delete().catch(err => {
      console.error(err);
      success = false;
    });

  return success;
}

export async function close(liveId) {
  let success = true;
  const reserveDocRef = await db.collection(reserve_collectionName).doc(liveId);
  await reserveDocRef
    .set(
      {
        isEnded: true
      },
      { merge: true }
    )
    .catch(err => {
      console.error(err);
      success = false;
    });

  if (!success) return;

  const reserve = await reserveDocRef.get();
  return reserve.data();
}

async function getReserveDoc(liveId) {
  if (!liveId) return null;
  const reserve = await db.collection(reserve_collectionName).doc(liveId).get();
  return reserve.data();
}

async function updateReserve(liveId, id, replaceFunction) {
  let orgdoc = await getReserveDoc(liveId);
  if (!orgdoc) {
    return;
  }

  let orgList = orgdoc.list;
  if (!orgList || !orgList[id]) {
    return;
  }

  orgList[id] = await replaceFunction(orgList[id]);

  return await updateReserveDoc(liveId, orgList);
}

async function updateReserveDoc(liveId, reserveList) {
  let success = true;
  const reserveDocRef = await db.collection(reserve_collectionName).doc(liveId);
  await reserveDocRef
    .set(
      {
        liveId: liveId,
        list: reserveList,
        isEnded: false
      },
      { merge: false }
    )
    .catch(err => {
      console.error(err);
      success = false;
    });

  if (!success) return;

  const reserve = await reserveDocRef.get();
  return reserve.data();
}

function convertToPayload(id, doc, liveId) {
  let payload = {
    liveId: liveId,
    id: id,
    reserver: doc.reserver,
    point: doc.point,
    status: doc.status,
    startAt: DateTime.trimJSSeconds(doc.startAt.toDate()),
    endAt: DateTime.trimJSSeconds(doc.endAt.toDate())
  }

  payload["reservePoint"] = calcReservePoint(payload);
  return payload;
}

function calcReservePoint(payload) {
  let diff = payload.endAt - payload.startAt;
  return (Math.abs(diff) / (60 * 1000)) * POINT_PER_MINUTES;
}
